import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Plural, Trans } from "@lingui/react/macro";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  alpha,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { addDays, addMonths, differenceInDays, format, isSameDay, startOfMonth } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import Block from "@/components/Block";
import Counter from "@/components/Counter";
import DateRangeCalendar from "@/components/DateRangeCalendar";
import DateRangeCalendarSkeleton from "@/components/DateRangeCalendarSkeleton";
import TextButton from "@/components/TextButton";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { useFeatureFlags } from "@/contexts/FeaturesContext";
import { useAccommodationDayInfo, usePriceCheck } from "@/hooks";
import { primaryActionSx } from "@/theme/mui-theme";
import {
  computeStayLengthAvailability,
  findClosestAvailableNights,
  getValidArrivalDays,
} from "@/utils/alternativeDates";
import { analytics } from "@/utils/analytics";
import { formatDateRange, getDateFnsLocale } from "@/utils/booking";
import AlternativeDatesAirbnb from "./AlternativeDatesAirbnb";
import AlternativeDatesTimeline from "./AlternativeDatesTimeline";
import { getAlternativeSx } from "./alternativeDatesSx";

interface AlternativeDatesCalendarProps {
  onSearch: (dateFrom: Date, dateUntil: Date) => void;
  accoId: number;
}

export default function AlternativeDatesCalendar({
  onSearch,
  accoId,
}: AlternativeDatesCalendarProps) {
  const { state, actions } = useBookingContext();
  const { data: campgroundData, campsiteTypes } = useCampground();
  const { loading, data: dayInfoData, fetchDayInfo } = useAccommodationDayInfo();
  const [dayInfoError, setDayInfoError] = useState(false);
  const { price: localPrice, loading: priceLoading, checkPrice, formatPrice } = usePriceCheck();
  const { i18n, _ } = useLingui();
  const dateFnsLocale = getDateFnsLocale(i18n.locale);
  const { flags } = useFeatureFlags();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));

  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [stayDaySelected, setStayDaySelected] = useState<Date | null>(null);
  const [selectedAccoId, setSelectedAccoId] = useState(accoId);
  const [selectedNights, setSelectedNights] = useState(() =>
    differenceInDays(state.dateUntil!, state.dateFrom!),
  );
  const [nightsAutoAdjustedFrom, setNightsAutoAdjustedFrom] = useState<number | null>(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  // Ref so the auto-adjust effect only fires when availability data changes, not on every manual nights change
  const selectedNightsRef = useRef(selectedNights);
  selectedNightsRef.current = selectedNights;
  // Tracks the last date we sent a price request for, to deduplicate the double
  // onSelect call that DateRangeCalendar emits per click (DayPicker + handleDayClick)
  const lastFetchedFrom = useRef<Date | null>(null);

  const selectedCampsiteType = state.selectedCampsiteType;

  const availableAccos = useMemo(
    () =>
      campgroundData.campground.campsites.filter((c) => c.campsite_type === selectedCampsiteType),
    [campgroundData, selectedCampsiteType],
  );

  // Fetch day info when accommodation or nights change
  useEffect(() => {
    if (!selectedAccoId) return;

    const today = new Date();
    const dateFrom = format(today, "yyyy-MM-dd");
    const dateTo = format(addMonths(startOfMonth(today), 12), "yyyy-MM-dd");

    setDayInfoError(false);
    fetchDayInfo({ dateFrom, dateTo, accoId: selectedAccoId }).catch(() => setDayInfoError(true));
    setDateRange(undefined);
    setStayDaySelected(null);
  }, [selectedAccoId, fetchDayInfo]);

  // Pre-compute valid arrival days: days where the full N-night stay is bookable
  const validArrivalDays = useMemo(() => {
    if (!dayInfoData?.day_list || selectedNights <= 0) return [];

    return getValidArrivalDays(dayInfoData.day_list, selectedNights);
  }, [dayInfoData, selectedNights]);

  // For Variant I: all dates that fall within any valid stay period but aren't arrivals themselves.
  // E.g. if arrivals are Mar 1 & Mar 2 for 7 nights, validStayDays = Mar 2–8 ∪ Mar 3–9 minus arrivals.
  const validStayDays = useMemo(() => {
    if (flags.periodSelection !== "month-view-default" || selectedNights <= 0) return [];
    const arrivalSet = new Set(validArrivalDays.map((d) => format(d, "yyyy-MM-dd")));
    const staySet = new Set<string>();
    for (const arrivalDate of validArrivalDays) {
      for (let i = 1; i <= selectedNights; i++) {
        const key = format(addDays(arrivalDate, i), "yyyy-MM-dd");
        if (!arrivalSet.has(key)) staySet.add(key);
      }
    }
    return Array.from(staySet).map((s) => new Date(s));
  }, [flags.periodSelection, validArrivalDays, selectedNights]);

  const firstAvailableMonth = useMemo(
    () => (validArrivalDays.length > 0 ? startOfMonth(validArrivalDays[0]!) : undefined),
    [validArrivalDays],
  );

  const defaultMonthNearSearch = useMemo(() => {
    if (!state.dateFrom || validArrivalDays.length === 0) return firstAvailableMonth;
    const closest = validArrivalDays.reduce((a, b) =>
      Math.abs(differenceInDays(a, state.dateFrom!)) <=
      Math.abs(differenceInDays(b, state.dateFrom!))
        ? a
        : b,
    );
    return startOfMonth(closest);
  }, [state.dateFrom, validArrivalDays, firstAvailableMonth]);

  const disabledDays = useMemo(
    () => [
      { before: new Date() },
      (date: Date) => !validArrivalDays.some((d) => isSameDay(d, date)),
    ],
    [validArrivalDays],
  );

  const stayLengthAvailability = useMemo(
    () => (dayInfoData?.day_list ? computeStayLengthAvailability(dayInfoData.day_list) : {}),
    [dayInfoData],
  );

  // Auto-adjust selectedNights to the closest available option when data loads
  useEffect(() => {
    if (Object.keys(stayLengthAvailability).length === 0) return;
    const nights = selectedNightsRef.current;
    const candidate = findClosestAvailableNights(stayLengthAvailability, nights);
    if (candidate === null) return;
    setNightsAutoAdjustedFrom(nights);
    setSelectedNights(candidate);
    setDateRange(undefined);
  }, [stayLengthAvailability]);

  const getDisabledReason = useCallback(
    (date: Date): string | null => {
      if (validArrivalDays.some((d) => isSameDay(d, date))) return null;
      if (validStayDays.some((d) => isSameDay(d, date))) {
        return isMobile ? null : _(msg`Arrivals not allowed on this day`);
      }
      return null;
    },
    [validArrivalDays, validStayDays, isMobile, _],
  );

  const handleTypeChange = useCallback(
    (newType: string) => {
      actions.setSelectedCampsiteType(newType);
      const first = campgroundData.campground.campsites.find((c) => c.campsite_type === newType);
      if (first) setSelectedAccoId(first.id);
      setDateRange(undefined);
      setStayDaySelected(null);
    },
    [actions, campgroundData],
  );

  const handleAccoChange = useCallback((newId: number) => {
    setSelectedAccoId(newId);
    setDateRange(undefined);
    setStayDaySelected(null);
    lastFetchedFrom.current = null;
  }, []);

  const handleNightsChange = useCallback((n: number) => {
    setSelectedNights(n);
    setNightsAutoAdjustedFrom(null);
    setDateRange(undefined);
    setStayDaySelected(null);
  }, []);

  const handleSelectH = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from) {
        setDateRange(undefined);
        lastFetchedFrom.current = null;
        return;
      }
      setDateRange(range);
      if (!range.to) return;
      if (lastFetchedFrom.current && isSameDay(range.from, lastFetchedFrom.current)) return;
      lastFetchedFrom.current = range.from;
      checkPrice({
        dateFrom: range.from,
        dateUntil: range.to,
        campsiteId: selectedAccoId,
        persons: state.selectedPersons,
      }).catch(() => {});
    },
    [checkPrice, selectedAccoId, state.selectedPersons],
  );

  const handleSelect = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from) {
        setDateRange(undefined);
        lastFetchedFrom.current = null;
        return;
      }
      // Handles both { from, to: undefined } (DayPicker) and { from, to: from }
      // (DateRangeCalendar.handleDayClick re-selection) — both compute the same departure
      const newRange = { from: range.from, to: addDays(range.from, selectedNights) };
      setDateRange(newRange);
      // Call checkPrice synchronously here so React batches price=null + loading=true
      // together with the dateRange update — prevents stale price from flashing.
      // Guard against the double-fire: skip if we already fetched for this exact date.
      if (lastFetchedFrom.current && isSameDay(range.from, lastFetchedFrom.current)) return;
      lastFetchedFrom.current = range.from;
      checkPrice({
        dateFrom: newRange.from,
        dateUntil: newRange.to,
        campsiteId: selectedAccoId,
        persons: state.selectedPersons,
      }).catch(() => {});
    },
    [selectedNights, checkPrice, selectedAccoId, state.selectedPersons],
  );

  const unavailableModifier = useMemo(
    () => (date: Date) =>
      !validArrivalDays.some((d) => isSameDay(d, date)) &&
      !validStayDays.some((d) => isSameDay(d, date)),
    [validArrivalDays, validStayDays],
  );

  const handleSelectI = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from) return;
      if (validArrivalDays.some((d) => isSameDay(d, range.from!))) {
        setStayDaySelected(null);
        handleSelect(range);
      } else if (isMobile && validStayDays.some((d) => isSameDay(d, range.from!))) {
        setDateRange(undefined);
        setStayDaySelected(range.from);
      }
    },
    [handleSelect, validArrivalDays, validStayDays, isMobile],
  );

  const handleSearch = useCallback(() => {
    if (dateRange?.from && dateRange?.to) {
      analytics.capture("alternative_dates_selected", {
        accommodation_name: availableAccos.find((a) => a.id === selectedAccoId)?.name,
        accommodation_type: selectedCampsiteType,
        date_from: format(dateRange.from, "yyyy-MM-dd"),
        date_until: format(dateRange.to, "yyyy-MM-dd"),
        nights: selectedNights,
        source: "calendar",
      });
      onSearch(dateRange.from, dateRange.to);
    }
  }, [dateRange, onSearch, availableAccos, selectedAccoId, selectedCampsiteType, selectedNights]);

  const legendItems = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            background: alpha(theme.palette.success.main, 0.15),
            width: "12px",
            height: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: "success.dark",
            }}
          />
        </Box>
        <Typography variant="body2">Arrival available</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{ background: alpha(theme.palette.success.main, 0.1), width: "12px", height: "12px" }}
        ></Box>
        <Typography variant="body2">Stay only</Typography>
      </Stack>
    </Stack>
  );

  const helpButton = (
    <Tooltip title={<Trans>Why is a specific date unavailable for booking?</Trans>}>
      <IconButton size="small" onClick={() => setHelpDialogOpen(true)}>
        <HelpOutlineIcon fontSize="small" sx={{ color: "text.accent" }} />
      </IconButton>
    </Tooltip>
  );

  return (
    <Stack spacing={1.5} sx={{ flex: { xs: 1, sm: "initial" }, minHeight: { xs: 0 } }}>
      {/* Title row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={{ xs: "flex-start", sm: "center" }}
        flexWrap="wrap"
        gap={1}
        pt={{ xs: 1.5, sm: 0 }}
        pl={{ xs: 2, sm: 0 }}
        pr={{ xs: 6, sm: 0 }}
        sx={{ flexShrink: 0 }}
      >
        {loading ? (
          <Skeleton variant="text" width={280} height={36} />
        ) : flags.titleConfiguration === "text_with_type_selector" ? (
          <>
            <Typography variant="h3" fontWeight={600}>
              <Trans>Alternative availabilities for</Trans>
            </Typography>
            <Select
              variant="standard"
              value={selectedCampsiteType ?? ""}
              onChange={(e) => handleTypeChange(e.target.value)}
              disableUnderline
              sx={{
                fontWeight: 600,
                fontSize: "h3.fontSize",
                textDecoration: "underline",
                color: "text.accent",
              }}
            >
              {campsiteTypes.map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </>
        ) : flags.titleConfiguration === "text_with_option_selector" ? (
          <>
            <Typography variant="h3" fontWeight={600}>
              <Trans>Alternative availabilities for</Trans>
            </Typography>
            <Select
              variant="standard"
              value={selectedAccoId}
              onChange={(e) => handleAccoChange(Number(e.target.value))}
              disableUnderline
              sx={{
                fontWeight: 600,
                fontSize: "h3.fontSize",
                textDecoration: "underline",
                color: "text.accent",
              }}
            >
              {availableAccos.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </>
        ) : (
          <Typography variant="h3" fontWeight={600} my={1}>
            <Trans>
              Alternative arrival dates for {selectedNights}{" "}
              <Plural value={selectedNights} one="night" other="nights" />
            </Trans>
          </Typography>
        )}
      </Stack>
      {/* Auto-adjust warning */}
      {nightsAutoAdjustedFrom !== null && (
        <Box px={{ xs: 2, sm: 0 }} sx={{ flexShrink: 0 }}>
          <Alert severity="warning">
            <Trans>
              No <Plural value={nightsAutoAdjustedFrom} one="# night" other="# nights" /> stay is
              available. Showing <Plural value={selectedNights} one="# night" other="# nights" />{" "}
              instead — the closest available option.
            </Trans>
          </Alert>
        </Box>
      )}
      {/* Calendar */}
      <Block
        sx={{
          borderWidth: { xs: 0, sm: 1 },
          borderRadius: { xs: 0, sm: 5 },
          p: 0,
          overflow: "hidden",
          flex: { xs: 1, sm: "initial" },
          display: { xs: "flex", sm: "block" },
          flexDirection: { xs: "column" },
          minHeight: { xs: 0, sm: "auto" },
        }}
      >
        <Stack spacing={1.5} sx={{ flexShrink: 0 }}>
          {/* Filters row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            px={{ xs: 0, sm: 2 }}
            pt={{ xs: 0, sm: 2 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              width={{ xs: "100%", sm: "auto" }}
            >
              {/* Accommodation option */}
              {flags.showAccommodationOptionSelector && (
                <Select
                  size="small"
                  value={selectedAccoId}
                  onChange={(e) => handleAccoChange(Number(e.target.value))}
                  renderValue={(id) => availableAccos.find((a) => a.id === id)?.name ?? ""}
                  sx={{ display: { xs: "none", sm: "block" } }}
                >
                  {availableAccos.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              )}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                flexGrow={1}
                px={1}
              >
                {/* Nights count */}
                {flags.nightsSelector !== "buttons" && flags.periodSelection !== "airbnb" && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 4,
                      px: 2,
                      py: 0.8,
                      overflow: "hidden",
                      flex: "none",
                      "& .MuiSvgIcon-root": {
                        fontSize: "1rem",
                      },
                    }}
                  >
                    <Typography>
                      <Trans>Nights</Trans>
                    </Typography>
                    <Counter
                      count={selectedNights}
                      onChange={handleNightsChange}
                      min={1}
                      max={30}
                    />
                  </Stack>
                )}
                {/* Reset dates button (mobile only) */}
                {flags.nightsSelector !== "buttons" && flags.periodSelection !== "airbnb" && (
                  <TextButton
                    onClick={() => setDateRange(undefined)}
                    disabled={!dateRange}
                    sx={{
                      display: { xs: "block", sm: "none" },
                      ml: "auto",
                      fontSize: "body2.fontSize",
                    }}
                  >
                    <Trans>Reset dates</Trans>
                  </TextButton>
                )}
              </Stack>

              {flags.periodSelection === "month-view-default" && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  pl={1.5}
                  pr={0.5}
                  py={0.5}
                  sx={{
                    width: "100%",
                    borderTop: "1px solid",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: { xs: "flex", sm: "none" },
                  }}
                >
                  {legendItems}
                  {helpButton}
                </Stack>
              )}
            </Stack>

            {/* Help button */}
            <Box sx={{ display: { xs: "none", sm: "block" } }}>{helpButton}</Box>
          </Stack>
          <Divider sx={{ display: { xs: "none", sm: "block" } }} />
        </Stack>
        <Box sx={{ flex: { xs: 1 }, overflowY: { xs: "auto" }, minHeight: { xs: 0 } }}>
          {(() => {
            if (loading) return <DateRangeCalendarSkeleton />;
            if (dayInfoError)
              return (
                <Box p={2}>
                  <Alert severity="error">
                    <Trans>Could not load availability data. Please try again.</Trans>
                  </Alert>
                </Box>
              );
            const viewProps = {
              validArrivalDays,
              selectedNights,
              selectedFrom: dateRange?.from,
              onArrivalSelect: (day: Date) => handleSelect({ from: day, to: undefined }),
              locale: dateFnsLocale,
            };
            if (flags.periodSelection === "airbnb")
              return (
                <AlternativeDatesAirbnb
                  dayList={dayInfoData?.day_list ?? {}}
                  dateRange={dateRange}
                  onSelect={handleSelectH}
                  locale={dateFnsLocale}
                />
              );
            if (flags.periodSelection === "week-view")
              return (
                <Box mt={{ xs: 2, sm: 0 }}>
                  <Divider sx={{ display: { xs: "block", sm: "none" } }} />
                  <AlternativeDatesTimeline
                    {...viewProps}
                    unavailableFrom={state.dateFrom ?? undefined}
                    unavailableUntil={state.dateUntil ?? undefined}
                  />
                  <Divider sx={{ display: { xs: "block", sm: "none" } }} />
                </Box>
              );
            if (flags.periodSelection === "month-view-default")
              return (
                <>
                  <DateRangeCalendar
                    key={selectedNights}
                    dateRange={dateRange}
                    onSelect={handleSelectI}
                    defaultMonth={defaultMonthNearSearch}
                    disabled={[]}
                    modifiers={{
                      unavailable: unavailableModifier,
                      validStay: validStayDays,
                      stayDaySelected: stayDaySelected ? [stayDaySelected] : [],
                    }}
                    modifiersClassNames={{
                      unavailable: "unavailable",
                      validStay: "validStay",
                      stayDaySelected: "stayDaySelected",
                    }}
                    paletteColor="success"
                    sx={getAlternativeSx("month-view-default", theme, flags.disabledStrikethrough)}
                    getDayTooltip={getDisabledReason}
                  />
                  <Divider sx={{ display: { xs: "none", sm: "block" } }} />
                  <Stack
                    direction="row"
                    spacing={2}
                    p={2}
                    justifyContent="space-between"
                    sx={{ display: { xs: "none", sm: "flex" } }}
                  >
                    {legendItems}
                    <TextButton
                      onClick={() => setDateRange(undefined)}
                      disabled={!dateRange}
                      sx={{ fontSize: "body2.fontSize", ml: 1 }}
                    >
                      <Trans>Reset dates</Trans>
                    </TextButton>
                  </Stack>
                </>
              );
            return (
              <DateRangeCalendar
                key={selectedNights}
                dateRange={dateRange}
                onSelect={handleSelect}
                defaultMonth={firstAvailableMonth ?? state.dateFrom ?? new Date()}
                disabled={disabledDays}
                paletteColor="success"
                sx={getAlternativeSx(flags.periodSelection, theme, flags.disabledStrikethrough)}
              />
            );
          })()}
          {flags.nightsSelector !== "counter" && flags.periodSelection !== "airbnb" && <Divider />}
          {flags.nightsSelector !== "counter" && flags.periodSelection !== "airbnb" && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                overflowX: "auto",
                px: 2,
                py: 1,
                my: 1,
                display: { xs: "none", sm: "flex" },
              }}
            >
              {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => {
                const available = stayLengthAvailability[n];
                const selected = n === selectedNights;

                return (
                  <Button
                    key={n}
                    size="small"
                    variant="outlined"
                    disabled={!available}
                    onClick={() => handleNightsChange(n)}
                    sx={{
                      flexShrink: 0,
                      py: 1,
                      borderColor: n === selectedNights ? "primary.main" : "divider",
                      color: selected ? "text.accent" : "text.primary",
                      fontWeight: selected ? 600 : "normal",
                    }}
                  >
                    <Trans>
                      {n} <Plural value={n} one="night" other="nights" />
                    </Trans>
                  </Button>
                );
              })}
            </Stack>
          )}
        </Box>
      </Block>
      {/* Bottom action row */}
      <Block
        sx={{
          borderWidth: { xs: 0, sm: 1 },
          borderRadius: { xs: 0, sm: 5 },
          boxShadow: { xs: "0px -6px 6px rgba(0, 0, 0, 0.08)", sm: "none" },
          p: 1.5,
          flexShrink: 0,
          mt: { xs: "0 !important", sm: "12px !important" },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent={dateRange?.from && dateRange?.to ? "space-between" : "center"}
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1}
        >
          {dateRange?.from && dateRange?.to ? (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                {formatDateRange([dateRange.from, dateRange.to], i18n.locale)} (
                <Plural value={selectedNights} one="# night" other="# nights" />)
              </Typography>
              {priceLoading ? (
                <Skeleton variant="text" width={100} />
              ) : localPrice?.total ? (
                <Typography variant="h3" color="text.accent">
                  <Trans>from</Trans> {formatPrice(localPrice.total)}
                </Typography>
              ) : (
                <Typography>
                  <Trans>No price available</Trans>
                </Typography>
              )}
            </Stack>
          ) : stayDaySelected ? (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              <Trans>Arrivals not allowed on this day</Trans>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {flags.periodSelection === "month-view-a" ||
              flags.periodSelection === "month-view-b" ||
              flags.periodSelection === "month-view-c" ||
              flags.periodSelection === "month-view-default" ? (
                <Trans>Please select an arrival date</Trans>
              ) : (
                <Trans>Please select a stay period</Trans>
              )}
            </Typography>
          )}
          {dateRange?.from && dateRange?.to && (
            <Button
              variant="contained"
              size="large"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={!dateRange?.from || !dateRange?.to || !localPrice}
              sx={{ ...primaryActionSx, flexShrink: 0, px: 5, width: { xs: "100%", sm: "auto" } }}
            >
              <Trans>View offers</Trans>
            </Button>
          )}
        </Stack>
      </Block>
      {/* Help dialog */}
      <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)}>
        <DialogTitle sx={{ pr: 6 }}>
          <Trans>Why is a specific date unavailable for booking?</Trans>
          <IconButton
            onClick={() => setHelpDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <List dense disablePadding sx={{ listStyleType: "disc", pl: 2 }}>
            <ListItem sx={{ display: "list-item" }} disableGutters>
              <Typography variant="body2">
                <Trans>
                  The accommodation is closed or unavailable on one or more nights of your stay
                </Trans>
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item" }} disableGutters>
              <Typography variant="body2">
                <Trans>Arrivals or departures aren't allowed on this day of the week</Trans>
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item" }} disableGutters>
              <Typography variant="body2">
                <Trans>
                  The stay length doesn't meet the minimum or maximum nights requirement
                </Trans>
              </Typography>
            </ListItem>
            <ListItem sx={{ display: "list-item" }} disableGutters>
              <Typography variant="body2">
                <Trans>This date is too soon — a minimum advance booking period applies</Trans>
              </Typography>
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
